import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  { v: "house", l: "House & Siding Wash" },
  { v: "driveway", l: "Driveway / Concrete" },
  { v: "roof", l: "Soft-Wash Roof" },
  { v: "deck", l: "Deck / Patio / Fence" },
  { v: "commercial", l: "Commercial Property" },
  { v: "gutter", l: "Gutter Cleaning" },
  { v: "vehicle", l: "Truck / Heavy Equipment" },
  { v: "other", l: "Something Else" },
];

const PROPERTY = [
  { v: "residential", l: "Residential" },
  { v: "commercial", l: "Commercial" },
  { v: "fleet", l: "Fleet / Equipment" },
];

const inputClass =
  "rounded-none bg-transparent border-[#2A2A2A] text-white placeholder:text-gray-600 focus-visible:border-[#FFD600] focus-visible:ring-0 focus-visible:ring-offset-0 h-12";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    property_type: "",
    address: "",
    preferred_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Please fill in name, email, phone, and service.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(
        (k) => payload[k] === "" && delete payload[k]
      );
      await axios.post(`${API}/quotes`, payload);
      toast.success("Quote request sent! We'll call you within 24 hours.");
      setDone(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        property_type: "",
        address: "",
        preferred_date: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="px-6 md:px-12 lg:px-20 py-24 md:py-32 border-b border-[#2A2A2A] bg-[#080808]"
    >
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#FFD600]">
            05 — Free Quote
          </span>
          <h2 className="font-display font-bold uppercase tracking-tight leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mt-4">
            Tell us
            <br />
            what's
            <br />
            <span className="text-[#FFD600]">filthy.</span>
          </h2>
          <p className="mt-6 text-gray-400 leading-relaxed max-w-sm">
            Fill out the form. Send a photo if you've got one. We'll come back with a flat-rate
            quote — usually same day.
          </p>

          <div className="mt-10 border border-[#2A2A2A] p-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
              Prefer to talk?
            </div>
            <a
              href="tel:+15066084350"
              data-testid="quote-phone-link"
              className="block font-display font-bold text-2xl text-white hover:text-[#FFD600]"
            >
              (506) 608-4350
            </a>
            <a
              href="mailto:stevenunderhill40@gmail.com"
              data-testid="quote-email-link"
              className="block text-sm text-gray-400 hover:text-white mt-1 break-all"
            >
              stevenunderhill40@gmail.com
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="quote-success"
              className="border border-[#FFD600] bg-[#0A0A0A] p-10 md:p-14 min-h-[420px] flex flex-col justify-center"
            >
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FFD600] mb-4">
                Request Received
              </div>
              <h3 className="font-display font-bold uppercase text-3xl md:text-5xl leading-tight">
                We're on it.
              </h3>
              <p className="mt-4 text-gray-400 max-w-md">
                Expect a call or text within 24 hours (usually a lot sooner). Thanks for choosing
                Big Bubbles.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="quote-reset-btn"
                className="mt-8 self-start border border-white/30 text-white font-bold uppercase tracking-wider px-6 py-3 hover:border-[#FFD600] hover:text-[#FFD600] transition-colors text-sm"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="border border-[#2A2A2A] bg-[#0A0A0A] p-8 md:p-10 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400" htmlFor="qf-name">
                    Full Name *
                  </Label>
                  <Input
                    id="qf-name"
                    data-testid="qf-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className={inputClass}
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400" htmlFor="qf-email">
                    Email *
                  </Label>
                  <Input
                    id="qf-email"
                    data-testid="qf-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className={inputClass}
                    placeholder="you@email.ca"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400" htmlFor="qf-phone">
                    Phone *
                  </Label>
                  <Input
                    id="qf-phone"
                    data-testid="qf-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className={inputClass}
                    placeholder="(506) 555-0123"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400" htmlFor="qf-date">
                    Preferred Date
                  </Label>
                  <Input
                    id="qf-date"
                    data-testid="qf-date"
                    type="date"
                    value={form.preferred_date}
                    onChange={(e) => update("preferred_date", e.target.value)}
                    className={`${inputClass} [color-scheme:dark]`}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400">
                    Service *
                  </Label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger
                      data-testid="qf-service"
                      className="rounded-none bg-transparent border-[#2A2A2A] text-white h-12 focus:ring-0 focus:border-[#FFD600] data-[placeholder]:text-gray-600"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] text-white">
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.v}
                          value={s.v}
                          data-testid={`qf-service-${s.v}`}
                          className="rounded-none focus:bg-[#FFD600] focus:text-black"
                        >
                          {s.l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400">
                    Property Type
                  </Label>
                  <Select
                    value={form.property_type}
                    onValueChange={(v) => update("property_type", v)}
                  >
                    <SelectTrigger
                      data-testid="qf-property"
                      className="rounded-none bg-transparent border-[#2A2A2A] text-white h-12 focus:ring-0 focus:border-[#FFD600] data-[placeholder]:text-gray-600"
                    >
                      <SelectValue placeholder="Residential / Commercial / Fleet" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] text-white">
                      {PROPERTY.map((p) => (
                        <SelectItem
                          key={p.v}
                          value={p.v}
                          className="rounded-none focus:bg-[#FFD600] focus:text-black"
                        >
                          {p.l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400" htmlFor="qf-address">
                  Job Address
                </Label>
                <Input
                  id="qf-address"
                  data-testid="qf-address"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  className={inputClass}
                  placeholder="123 Main St, Saint John, NB"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-[0.25em] text-gray-400" htmlFor="qf-message">
                  Tell us about the job
                </Label>
                <Textarea
                  id="qf-message"
                  data-testid="qf-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="rounded-none bg-transparent border-[#2A2A2A] text-white placeholder:text-gray-600 focus-visible:border-[#FFD600] focus-visible:ring-0 focus-visible:ring-offset-0 min-h-[120px]"
                  placeholder="Two-storey vinyl siding, mossy north side, deck stains... whatever you've got."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="qf-submit"
                className="w-full sm:w-auto bg-[#FFD600] text-black font-bold uppercase tracking-wider px-8 py-5 inline-flex items-center justify-center gap-3 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_rgba(255,255,255,0.25)] transition-all disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Request Free Quote <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
